/**
 * Phase 1 Visual Email Editor — block-based composer for the Admin Email Hub.
 *
 * Layout (three columns on desktop):
 *   [ Block palette ] [ Canvas + Properties ] [ Live Preview ]
 *
 * Powered by the backend endpoints in email_hub.py + email_blocks.py:
 *   - GET  /email-hub/brand-assets      → auto-branding presets
 *   - PATCH /email-hub/templates/{key}  → save (also snapshots the previous version)
 *   - POST /email-hub/preview           → server-rendered HTML (source of truth)
 *   - POST /email-hub/test-send         → real email to any address
 *   - GET  /email-hub/templates/{key}/versions
 *   - POST /email-hub/templates/{key}/restore/{version_id}
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Image as ImageIcon, Type, Heading, Code2, MousePointerClick, Minus, Square,
  ArrowUp, ArrowDown, Trash2, Eye, Send, Save, History, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_LIBRARY = [
  { type: "logo",    label: "Logo",    icon: ImageIcon, defaults: { src: "auto", align: "center", width: 96 } },
  { type: "header",  label: "Heading", icon: Heading,   defaults: { text: "New heading", align: "left", size: "h1" } },
  { type: "text",    label: "Text",    icon: Type,      defaults: { content: "Your paragraph copy goes here. Use **bold** and [links](https://…) inline." } },
  { type: "html",    label: "HTML",    icon: Code2,     defaults: { content: "<div style=\"padding: 16px;\">Paste email-safe HTML here.</div>" } },
  { type: "button",  label: "Button",  icon: MousePointerClick, defaults: { label: "Click me", href: "https://theditchcollective.co.nz", align: "center" } },
  { type: "divider", label: "Divider", icon: Minus,     defaults: {} },
  { type: "image",   label: "Image",   icon: ImageIcon, defaults: { src: "", alt: "", width: 520, align: "center" } },
  { type: "spacer",  label: "Spacer",  icon: Square,    defaults: { height: 24 } },
];

function uid() { return Math.random().toString(36).slice(2, 10); }

/**
 * Legacy-body rescue: some seeded templates carry their content in a flat
 * `body` field (markdown-ish) and have zero `blocks`. Opening them in the
 * visual editor used to show a blank canvas. Convert body → one heading +
 * paragraphs of text + inline buttons so the admin sees the actual copy.
 */
function hydrateBlocksFromBody(body, headline) {
  const out = [];
  if (headline && String(headline).trim()) {
    out.push({ id: uid(), type: "header", props: { text: String(headline).trim(), align: "left", size: "h1" } });
  }
  const text = String(body || "").trim();
  if (!text) return out;
  const chunks = text.split(/\n\s*\n/);
  const linkOnly = /^\[([^\]]+)\]\((https?:\/\/[^)]+|\{\{[^}]+\}\})\)\s*$/;
  for (const raw of chunks) {
    const chunk = raw.trim();
    if (!chunk) continue;
    const m = chunk.match(linkOnly);
    if (m) {
      out.push({ id: uid(), type: "button", props: { label: m[1], href: m[2], align: "left" } });
    } else {
      out.push({ id: uid(), type: "text", props: { content: chunk } });
    }
  }
  return out;
}

export function VisualTemplateEditor({ templateKey, authHeaders, onClose, onSaved }) {
  const [tpl, setTpl] = useState(null);
  const [brand, setBrand] = useState("collective");
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [description, setDescription] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testContact, setTestContact] = useState("");
  const [sending, setSending] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionNote, setVersionNote] = useState("");
  const [brandAssets, setBrandAssets] = useState({});
  const previewTimer = useRef(null);

  // ── Load: template + brand assets ────────────────────────────────
  useEffect(() => {
    if (!templateKey) return;
    axios.get(`${API}/admin/email-hub/templates/${templateKey}`, { headers: authHeaders })
      .then(r => {
        const d = r.data;
        setTpl(d);
        setBrand(d.brand || "collective");
        setSubject(d.subject || "");
        setPreviewText(d.preview_text || "");
        setDescription(d.description || "");
        const rawBlocks = (d.blocks || []).map(b => ({ ...b, id: b.id || uid() }));
        // Legacy fallback: some seeded templates still ship with `body` and
        // no blocks. Hydrate the editor with that copy so the canvas never
        // looks blank when opening a template that was never migrated.
        if (rawBlocks.length === 0 && (d.body || "").trim()) {
          setBlocks(hydrateBlocksFromBody(d.body, d.headline));
        } else {
          setBlocks(rawBlocks);
        }
      })
      .catch(e => toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`));
    axios.get(`${API}/admin/email-hub/brand-assets`, { headers: authHeaders })
      .then(r => setBrandAssets(r.data.brands || {}))
      .catch(() => {});
  }, [templateKey, authHeaders]);

  // ── Live preview (debounced) ────────────────────────────────────
  const runPreview = useCallback(async (contactOverride = testContact) => {
    if (!templateKey) return;
    try {
      const r = await axios.post(
        `${API}/admin/email-hub/preview`,
        {
          template_key: templateKey,
          subject,
          preview_text: previewText,
          blocks,
          contact_email: (contactOverride || "").trim() || null,
        },
        { headers: authHeaders },
      );
      setPreviewHtml(r.data.html || "");
    } catch (e) {
      console.warn("preview failed:", e?.message);
    }
  }, [templateKey, subject, previewText, blocks, testContact, authHeaders]);

  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(runPreview, 350);
    return () => clearTimeout(previewTimer.current);
  }, [runPreview]);

  // ── Block operations ─────────────────────────────────────────────
  const addBlock = (type) => {
    const spec = BLOCK_LIBRARY.find(b => b.type === type);
    const nb = { id: uid(), type, props: { ...spec.defaults } };
    setBlocks([...blocks, nb]);
    setActiveId(nb.id);
  };
  const removeBlock = (id) => {
    setBlocks(blocks.filter(b => b.id !== id));
    if (activeId === id) setActiveId(null);
  };
  const moveBlock = (id, delta) => {
    const idx = blocks.findIndex(b => b.id === id);
    if (idx < 0) return;
    const next = idx + delta;
    if (next < 0 || next >= blocks.length) return;
    const copy = [...blocks];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    setBlocks(copy);
  };
  const updateBlockProps = (id, patch) => {
    setBlocks(blocks.map(b => b.id === id ? { ...b, props: { ...b.props, ...patch } } : b));
  };

  const active = blocks.find(b => b.id === activeId) || null;

  // ── Save & Activate ─────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/email-hub/templates/${templateKey}`,
        {
          subject,
          preview_text: previewText,
          description,
          blocks,
          version_note: versionNote || "",
        },
        { headers: authHeaders },
      );
      toast.success("Saved & activated — the next send uses this version.");
      setVersionNote("");
      onSaved?.();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  // ── Send test ───────────────────────────────────────────────────
  const sendTest = async () => {
    if (!testEmail.includes("@")) return toast.error("Enter a valid test email.");
    setSending(true);
    try {
      // Save first so the test sends what the admin sees.
      await axios.patch(`${API}/admin/email-hub/templates/${templateKey}`,
        { subject, preview_text: previewText, blocks, version_note: "(auto: pre-test-send save)" },
        { headers: authHeaders });
      const r = await axios.post(`${API}/admin/email-hub/test-send`,
        { template_key: templateKey, to_email: testEmail, context: {} },
        { headers: authHeaders });
      if (r.data.ok) toast.success(`Test sent to ${testEmail}.`);
      else toast.error(`Test failed: ${r.data.error || "unknown"}`);
      setTestOpen(false);
    } catch (e) {
      toast.error(`Test failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSending(false); }
  };

  // ── Versions ────────────────────────────────────────────────────
  const openVersions = async () => {
    setVersionsOpen(true);
    const r = await axios.get(`${API}/admin/email-hub/templates/${templateKey}/versions`, { headers: authHeaders });
    setVersions(r.data.versions || []);
  };
  const restore = async (versionId) => {
    if (!window.confirm("Restore this earlier version? The current one will be snapshot'd first, so this is reversible.")) return;
    await axios.post(`${API}/admin/email-hub/templates/${templateKey}/restore/${versionId}`, {}, { headers: authHeaders });
    toast.success("Restored — reloading editor.");
    setVersionsOpen(false);
    // reload template
    const r = await axios.get(`${API}/admin/email-hub/templates/${templateKey}`, { headers: authHeaders });
    const d = r.data;
    setTpl(d); setSubject(d.subject || ""); setPreviewText(d.preview_text || "");
    const rawBlocks = (d.blocks || []).map(b => ({ ...b, id: b.id || uid() }));
    if (rawBlocks.length === 0 && (d.body || "").trim()) {
      setBlocks(hydrateBlocksFromBody(d.body, d.headline));
    } else {
      setBlocks(rawBlocks);
    }
  };

  if (!tpl) {
    return <div className="p-8 text-center text-[#6F8FA8]">Loading template…</div>;
  }

  const brandCfg = brandAssets[brand] || {};
  return (
    <div className="fixed inset-0 bg-[#F5F5F2] z-50 flex flex-col" data-testid="visual-editor">
      {/* Toolbar */}
      <div className="bg-[#0D2F4D] text-white px-6 py-3 flex flex-wrap gap-3 items-center border-b border-black/20">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="ve-close" className="text-white hover:bg-white/10">
            <X className="h-4 w-4 mr-1"/>Close
          </Button>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#C6D21F] font-bold">Editing template</div>
            <div className="font-mono text-sm">{templateKey}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-2">
          <label className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Brand</label>
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white" data-testid="ve-brand">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="collective">The Ditch Collective</SelectItem>
              <SelectItem value="careers">The Ditch Careers</SelectItem>
            </SelectContent>
          </Select>
          {brandCfg.logo_url && (
            <img src={brandCfg.logo_url} alt="" className="w-7 h-7 rounded-full bg-white p-0.5"/>
          )}
          <Badge className="text-[10px] bg-[#C6D21F] text-[#0D2F4D]">Auto-branded</Badge>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={openVersions} data-testid="ve-versions" className="text-white hover:bg-white/10">
            <History className="h-4 w-4 mr-1"/>Versions
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setTestOpen(true)} data-testid="ve-test-send" className="text-white hover:bg-white/10">
            <Send className="h-4 w-4 mr-1"/>Send test
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="ve-save" className="bg-[#C6D21F] hover:bg-[#C6D21F]/90 text-[#0D2F4D]">
            <Save className="h-4 w-4 mr-1"/>{saving ? "Saving…" : "Save & Activate"}
          </Button>
        </div>
      </div>

      {/* 3-column workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT — palette + subject */}
        <aside className="w-64 flex-shrink-0 border-r border-[#E4E7EA] bg-white overflow-y-auto" data-testid="ve-palette">
          <div className="p-4 border-b border-[#E4E7EA]">
            <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ve-subject" placeholder="Subject line"/>
            <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mt-3 mb-1">Preview text</label>
            <Input value={previewText} onChange={(e) => setPreviewText(e.target.value)} data-testid="ve-preview-text" placeholder="What shows in inbox previews"/>
            <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mt-3 mb-1">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="ve-description" placeholder="Internal notes"/>
          </div>
          <div className="p-4">
            <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-3">Add block</p>
            <div className="grid grid-cols-2 gap-2">
              {BLOCK_LIBRARY.map(({ type, label, icon: Icon }) => (
                <button
                  key={type}
                  onClick={() => addBlock(type)}
                  data-testid={`ve-add-${type}`}
                  className="border border-[#E4E7EA] hover:border-[#0D2F4D] rounded-xl p-3 flex flex-col items-center gap-1 transition-colors group"
                >
                  <Icon size={20} className="text-[#0D2F4D] group-hover:text-[#C6D21F]"/>
                  <span className="text-[11px] font-bold text-[#0D2F4D]">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* MIDDLE — canvas + properties (below) */}
        <section className="flex-1 min-w-0 overflow-y-auto" data-testid="ve-canvas">
          <div className="max-w-lg mx-auto p-6 space-y-3">
            {blocks.length === 0 && (
              <div className="border-2 border-dashed border-[#D6DCE3] rounded-2xl p-12 text-center text-[#6F8FA8]">
                Add a block from the left to start.
              </div>
            )}
            {blocks.map((b, idx) => (
              <BlockRow
                key={b.id}
                block={b}
                index={idx}
                total={blocks.length}
                active={activeId === b.id}
                onSelect={() => setActiveId(b.id)}
                onMove={(delta) => moveBlock(b.id, delta)}
                onRemove={() => removeBlock(b.id)}
              />
            ))}
          </div>

          {active && (
            <div className="border-t border-[#E4E7EA] bg-white p-6 sticky bottom-0" data-testid="ve-properties">
              <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-3">
                Editing <span className="text-[#0D2F4D]">{active.type}</span> block
              </p>
              <BlockPropsForm block={active} onChange={(patch) => updateBlockProps(active.id, patch)} />
            </div>
          )}
        </section>

        {/* RIGHT — live preview */}
        <aside className="w-[420px] flex-shrink-0 border-l border-[#E4E7EA] bg-[#EDEDEA] overflow-y-auto" data-testid="ve-preview">
          <div className="p-4 flex items-center justify-between border-b border-[#E4E7EA] bg-white">
            <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold flex items-center gap-1.5">
              <Eye size={12}/>Live preview
            </p>
            <Input
              value={testContact}
              onChange={(e) => setTestContact(e.target.value)}
              placeholder="Preview as… email"
              className="w-56 text-xs"
              data-testid="ve-preview-as"
            />
          </div>
          <div className="p-3">
            <iframe
              srcDoc={previewHtml || "<div style='padding:32px;font-family:sans-serif;color:#6F8FA8'>preview will render here…</div>"}
              title="preview"
              className="w-full h-[900px] bg-white rounded-xl border border-[#E4E7EA]"
              sandbox=""
              data-testid="ve-preview-frame"
            />
          </div>
        </aside>
      </div>

      {/* ── Send test dialog ──────────────────────────────────── */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="ve-test-dialog">
          <DialogHeader><DialogTitle>Send a test email</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6F8FA8]">Uses sample merge tokens (first_name: "Sam" etc.) so you can proof-read before firing at real contacts.</p>
          <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@theditchcollective.co.nz" data-testid="ve-test-to"/>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={sendTest} disabled={sending} className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" data-testid="ve-test-fire">
              {sending ? "Sending…" : "Send test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Versions dialog ───────────────────────────────────── */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-2xl" data-testid="ve-versions-dialog">
          <DialogHeader><DialogTitle>Version history — {templateKey}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {versions.length === 0 && <p className="text-sm text-[#6F8FA8]">No prior versions yet. Every save creates a snapshot from now on.</p>}
            {versions.map(v => (
              <div key={v.id} className="border border-[#E4E7EA] rounded-xl p-3 flex items-start justify-between gap-3" data-testid={`ve-version-${v.id}`}>
                <div className="text-sm flex-1 min-w-0">
                  <div className="font-semibold text-[#0D2F4D]">{v.subject || "(no subject)"}</div>
                  <div className="text-xs text-[#6F8FA8]">
                    {new Date(v.snapshot_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                    {" · "}{(v.blocks || []).length} blocks
                    {v.version_note ? ` · ${v.version_note}` : ""}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => restore(v.id)} data-testid={`ve-restore-${v.id}`}>Restore</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ── One block row on the canvas — shows a lightweight rendition + inline actions ─── */
function BlockRow({ block, index, total, active, onSelect, onMove, onRemove }) {
  const cls = "group border rounded-xl transition-all cursor-pointer " +
    (active
      ? "border-[#C6D21F] shadow-[0_0_0_3px_rgba(198,210,31,0.25)] bg-white"
      : "border-[#E4E7EA] bg-white hover:border-[#0D2F4D]");
  return (
    <div className={cls} onClick={onSelect} data-testid={`ve-block-row-${block.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#F2F2EF] bg-[#FAFAF7] rounded-t-xl">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8]">{block.type}</span>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onMove(-1); }} disabled={index === 0}
            className="p-1 text-[#6F8FA8] hover:text-[#0D2F4D] disabled:opacity-30" data-testid={`ve-move-up-${block.id}`}>
            <ArrowUp size={14}/>
          </button>
          <button onClick={(e) => { e.stopPropagation(); onMove(1); }} disabled={index === total - 1}
            className="p-1 text-[#6F8FA8] hover:text-[#0D2F4D] disabled:opacity-30" data-testid={`ve-move-down-${block.id}`}>
            <ArrowDown size={14}/>
          </button>
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-1 text-red-600 hover:text-red-800" data-testid={`ve-remove-${block.id}`}>
            <Trash2 size={14}/>
          </button>
        </div>
      </div>
      <div className="p-4">
        <BlockThumb block={block} />
      </div>
    </div>
  );
}

function BlockThumb({ block }) {
  const p = block.props || {};
  switch (block.type) {
    case "logo": return <div className="text-center text-xs text-[#6F8FA8]">— logo — <span className="text-[10px] block mt-1">{p.align || "center"} · {p.width || 96}px</span></div>;
    case "header": return <div className="font-bold text-[#0D2F4D] text-lg" style={{ textAlign: p.align || "left" }}>{p.text || "(empty heading)"}</div>;
    case "text": return <div className="text-sm text-[#0D2F4D] whitespace-pre-wrap">{p.content || "(empty text)"}</div>;
    case "html": return <pre className="max-h-28 overflow-hidden whitespace-pre-wrap font-mono text-[11px] text-[#0D2F4D]">{p.content || "(empty HTML)"}</pre>;
    case "button": return <div className="text-center"><span className="inline-block bg-[#C6D21F] text-[#0D2F4D] font-bold text-xs px-4 py-2 rounded-full">{p.label || "Button"}</span><div className="text-[10px] text-[#6F8FA8] mt-1 truncate">→ {p.href || "(no link)"}</div></div>;
    case "divider": return <div className="border-t border-[#D6DCE3]"/>;
    case "image": return <div className="text-xs text-[#6F8FA8]">🖼 {p.src || "(no image url)"}</div>;
    case "spacer": return <div className="text-[10px] text-[#6F8FA8] text-center">↕ {p.height || 24}px space</div>;
    default: return null;
  }
}

/* ── Properties form — different fields per block type ─── */
function BlockPropsForm({ block, onChange }) {
  const p = block.props || {};
  const field = (name, label, extra = {}) => (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">{label}</label>
      <Input value={p[name] || ""} onChange={(e) => onChange({ [name]: e.target.value })} data-testid={`ve-prop-${name}`} {...extra}/>
    </div>
  );
  const alignSelect = () => (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Align</label>
      <Select value={p.align || "left"} onValueChange={(v) => onChange({ align: v })}>
        <SelectTrigger data-testid="ve-prop-align"><SelectValue/></SelectTrigger>
        <SelectContent>
          <SelectItem value="left">Left</SelectItem>
          <SelectItem value="center">Center</SelectItem>
          <SelectItem value="right">Right</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  switch (block.type) {
    case "logo":
      return (
        <div className="grid grid-cols-3 gap-3">
          {field("src", "Source URL (or 'auto')")}
          {alignSelect()}
          {field("width", "Width (px)", { type: "number" })}
        </div>
      );
    case "header":
      return (
        <div className="space-y-3">
          <Textarea value={p.text || ""} onChange={(e) => onChange({ text: e.target.value })} rows={2} data-testid="ve-prop-text"/>
          <div className="grid grid-cols-3 gap-3">
            {alignSelect()}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Size</label>
              <Select value={p.size || "h1"} onValueChange={(v) => onChange({ size: v })}>
                <SelectTrigger data-testid="ve-prop-size"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="h1">Large (H1)</SelectItem>
                  <SelectItem value="h2">Medium (H2)</SelectItem>
                  <SelectItem value="h3">Small (H3)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {field("color", "Colour (hex)")}
          </div>
        </div>
      );
    case "text":
      return (
        <div className="space-y-3">
          <Textarea value={p.content || ""} onChange={(e) => onChange({ content: e.target.value })} rows={6} data-testid="ve-prop-content" placeholder="Supports **bold**, [link text](url) and {{merge_tokens}}."/>
          <div className="grid grid-cols-2 gap-3">
            {alignSelect()}
            {field("color", "Colour (hex)")}
          </div>
        </div>
      );
    case "html":
      return (
        <div className="space-y-2">
          <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block">Raw HTML</label>
          <Textarea
            value={p.content || ""}
            onChange={(e) => onChange({ content: e.target.value })}
            rows={14}
            data-testid="ve-prop-html"
            className="font-mono text-xs min-h-[280px]"
            placeholder="Paste email-safe HTML. Scripts, iframes, forms, embeds and unsafe attributes are removed automatically."
          />
          <p className="text-xs text-[#6F8FA8]">Inline email styling is preserved. Executable or embedded content is stripped on save and again before send.</p>
        </div>
      );
    case "button":
      return (
        <div className="grid grid-cols-2 gap-3">
          {field("label", "Label")}
          {field("href", "Link URL (supports {{tokens}})")}
          {alignSelect()}
          {field("bg", "Background (hex)")}
        </div>
      );
    case "divider":
      return field("color", "Line colour (hex)");
    case "image":
      return (
        <div className="grid grid-cols-2 gap-3">
          {field("src", "Image URL")}
          {field("alt", "Alt text")}
          {field("width", "Width (px)", { type: "number" })}
          {alignSelect()}
        </div>
      );
    case "spacer":
      return field("height", "Height (px)", { type: "number" });
    default:
      return null;
  }
}
